import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  supportedMetricsSelector,
  intervalSelector,
  getFirstAppliedFilter,
} from '@store/common/selectors';
import { allDimensionsListSelector } from '@store/common/dimensionList/selectors';

import { createTempDashboard } from '@store/tempDashboard/actions';
import {
  getAppEventsPreset,
  getServerSidePerformancePreset,
} from '@src/components/common/Dashboard/UseCases/configs/eco';
import {
  getValidateDimensionFunc,
  getValidateMetricFunc,
} from '@src/components/common/Dashboard/UseCases/utils';
import { drilldownDashboardType } from '@src/components/common/Dashboard/UseCases';
import { get } from 'lodash';
import apptracker from '@src/utils/sensor/apptracker';
import { WIDGET_DRILLDOWN } from '@src/utils/sensor/track';

const getPresetConfigFunc = (category: string) => {
  return {
    events: getAppEventsPreset,
    network: getServerSidePerformancePreset,
  }[category];
};

function useDrillDownToPreset({ category, metrics = [] }) {
  const supportedMetrics = useSelector(supportedMetricsSelector) as object;
  const dimensionList = useSelector(allDimensionsListSelector) as [];
  const interval = useSelector(intervalSelector);
  const filter = useSelector(getFirstAppliedFilter);
  const dispatch = useDispatch();

  const handleActions = useCallback(() => {
    const getPresetConfig = getPresetConfigFunc(category);
    if (!getPresetConfig) {
      return;
    }

    const { config, title, key } = getPresetConfig({
      validateDimension: getValidateDimensionFunc(dimensionList),
      validateMetric: getValidateMetricFunc(supportedMetrics),
    });

    const { common = {}, metricsChartContainer = {}, ...restConfig } = config;
    const presetMetrics = get(metricsChartContainer, 'metrics', []);

    const dashboardConfig = {
      ...restConfig,
      common: {
        ...common,
        interval,
        appliedFilters: [filter],
      },
      summary: {},
      usecaseKey: key,
      metricsChartContainer: {
        ...metricsChartContainer,
        metrics: Array.from(new Set([...metrics, ...presetMetrics])),
      },
    };

    dispatch(createTempDashboard(drilldownDashboardType, dashboardConfig, title));
    apptracker(WIDGET_DRILLDOWN, { title });
  }, [category, supportedMetrics, interval, dispatch, dimensionList, metrics]);

  return handleActions;
}

export default useDrillDownToPreset;
